import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import SEO from "../components/SEO";
import { teamSrcSet } from "../lib/img";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { Stagger, StaggerItem } from "../components/Reveal";
import { THERAPISTS, SITE_URL } from "../data/site";
import { breadcrumbSchema } from "../lib/schema";

const personSchema = (t) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: t.name,
  honorificSuffix: t.credentials,
  jobTitle: t.title,
  description: t.intro,
  image: `${SITE_URL}${t.photo}`,
  worksFor: { "@type": "MedicalBusiness", name: "NJ Therapy Online", url: SITE_URL },
});

export default function Team() {
  const [openId, setOpenId] = useState(null);
  const close = useCallback(() => setOpenId(null), []);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Meet the Team", path: "/team" },
  ];

  // Open a bio directly from /team#id links (e.g. from the About page).
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id && THERAPISTS.some((t) => t.id === id)) setOpenId(id);
  }, []);

  return (
    <>
      <SEO
        title="Meet the Team | Licensed Online Therapists in NJ"
        description="Meet the licensed clinicians at NJ Therapy Online, led by founder Dr. Reema Tajdar, LPC, PhD, ACS. Evidence-based virtual therapy across New Jersey."
        path="/team"
        schema={[breadcrumbSchema(crumbs), ...THERAPISTS.map(personSchema)]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow="Our clinicians"
        title="Meet the"
        accent="team"
        underline
        body="Licensed, compassionate clinicians using evidence-based care — online across New Jersey."
        testid="team-hero"
      />

      <section className="section" data-testid="team-list">
        <div className="container-site">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {THERAPISTS.map((t, i) => (
              <StaggerItem key={t.id} className="h-full">
                <article id={t.id} className="card card-hover h-full p-6 flex flex-col items-center text-center scroll-mt-28" data-testid={`therapist-${t.id}`}>
                  <div className="h-36 w-36 overflow-hidden rounded-full bg-[var(--sage-tint)] ring-4 ring-[var(--sage-tint)]">
                    <img src={t.photo} srcSet={teamSrcSet(t.photo)} sizes="144px" alt={`${t.name}, ${t.credentials}`} width="144" height="144" className="h-full w-full object-cover" loading={i < 3 ? "eager" : "lazy"} decoding="async" />
                  </div>
                  <h2 className="mt-5 text-xl md:text-2xl">
                    {t.name}, <span className="text-body font-medium">{t.credentials}</span>
                  </h2>
                  <p className="mt-1 text-sm font-medium text-[var(--sage)]">{t.title}</p>
                  {t.subtitle && (
                    <p className="mt-0.5 text-sm font-medium text-[var(--sage)]" data-testid={`therapist-subtitle-${t.id}`}>{t.subtitle}</p>
                  )}
                  <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                    {t.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="chip !text-xs !py-1 !px-2.5">{s}</span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenId(t.id)}
                    className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--sage)] hover:gap-3 transition-all"
                    aria-haspopup="dialog"
                    aria-controls={`bio-${t.id}`}
                    data-testid={`therapist-read-bio-${t.id}`}
                  >
                    Read bio <ArrowRight className="h-4 w-4" />
                  </button>
                  {/* Full bio kept in the markup (hidden) so it stays crawlable for SEO. */}
                  <div hidden>
                    <p>{t.intro}</p>
                    {t.bio.map((p) => (
                      <p key={p.slice(0, 24)}>{p}</p>
                    ))}
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {openId && typeof document !== "undefined" &&
        createPortal(
          <BioDialog therapist={THERAPISTS.find((t) => t.id === openId)} onClose={close} />,
          document.body
        )}

      <CTABand />
    </>
  );
}

function BioDialog({ therapist: t, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      id={`bio-${t.id}`}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`bio-title-${t.id}`}
      data-testid={`bio-dialog-${t.id}`}
    >
      <button type="button" aria-label="Close bio" className="fade-in absolute inset-0 bg-[rgba(29,33,28,.45)] backdrop-blur-[2px] cursor-default" style={{ animationDuration: "200ms" }} onClick={onClose} tabIndex={-1} />
      <div className="slide-in relative w-full sm:max-w-2xl max-h-[88vh] overflow-y-auto rounded-t-[1.75rem] sm:rounded-[1.75rem] bg-white p-6 md:p-9 shadow-[0_40px_80px_-30px_rgba(29,33,28,.5)]" style={{ animationDuration: "300ms" }}>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--sage-tint)]"
          aria-label="Close"
          data-testid={`bio-close-${t.id}`}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-5 pr-10">
          <img src={t.photo} srcSet={teamSrcSet(t.photo)} sizes="96px" alt="" width="96" height="96" className="h-20 w-20 md:h-24 md:w-24 shrink-0 rounded-full object-cover bg-[var(--sage-tint)]" loading="lazy" decoding="async" />
          <div>
            <h2 id={`bio-title-${t.id}`} className="text-2xl md:text-3xl">
              {t.name}, <span className="text-body font-medium">{t.credentials}</span>
            </h2>
            <p className="mt-1 text-sm font-medium text-[var(--sage)]">{t.title}</p>
            {t.subtitle && <p className="text-sm font-medium text-[var(--sage)]">{t.subtitle}</p>}
          </div>
        </div>
        <div className="mt-6" data-testid={`bio-text-${t.id}`}>
          <p className="text-[var(--ink)] leading-relaxed">{t.intro}</p>
          {t.bio.map((p) => (
            <p key={p.slice(0, 24)} className="mt-4 text-body leading-relaxed">{p}</p>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {t.specialties.map((s) => (
            <span key={s} className="chip">{s}</span>
          ))}
        </div>
        <Link to="/contact" className="mt-7 btn btn-primary" onClick={onClose} data-testid={`therapist-cta-${t.id}`}>
          Book a Session <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
